import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MapPinned, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shopify/lockere`;
const sel = "w-full rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-xs text-zinc-100";

export const ShopifyLockerPicker = ({ order, onSaved }) => {
  const c = order.customer || {};
  const [open, setOpen] = useState(false);
  const [counties, setCounties] = useState([]);
  const [county, setCounty] = useState("");
  const [city, setCity] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open || counties.length) return;
    axios.post(`${API}/counties_cities`, {}).then((r) => {
      const list = r.data.counties || [];
      setCounties(list);
      const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
      const m = list.find((x) => norm(x.county) === norm(c.shipping_county || c.billing_county));
      if (m) { setCounty(m.county); const ct = m.cities.find((x) => norm(x) === norm(order.locker_city || c.shipping_city)); if (ct) setCity(ct); }
    }).catch(() => toast.error("Nu am putut încărca județele"));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const cities = useMemo(() => (counties.find((x) => x.county === county) || {}).cities || [], [counties, county]);
  useEffect(() => {
    if (!county || !city) { setItems([]); return; }
    const t = setTimeout(() => axios.post(`${API}/search_lockers`, { county, city, q: q.trim(), limit: 100 })
      .then((r) => setItems(r.data.items || [])).catch(() => setItems([])), 250);
    return () => clearTimeout(t);
  }, [county, city, q]);
  const shown = items;

  const pick = async (l) => {
    setBusy(l.id);
    try {
      await axios.post(`${API}/set_locker`, { order_id: Number(order._shopify_id), locker_id: l.id, source: "admin" });
      toast.success(`Locker salvat: ${l.name} — apare și în comanda clientului`);
      setOpen(false); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy("");
  };

  if (!open) {
    return (
      <button data-testid="locker-pick-open-btn" onClick={() => setOpen(true)}
        className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-xs font-medium text-sky-200 hover:bg-sky-500/20">
        <MapPinned className="h-3.5 w-3.5" /> {order.locker_id && order.locker_id !== "?" ? "Schimbă lockerul" : "Alege lockerul"}
      </button>
    );
  }
  return (
    <div className="mt-3 space-y-2 border-t border-sky-500/20 pt-3" data-testid="locker-picker">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <select data-testid="locker-pick-county" className={sel} value={county} onChange={(e) => { setCounty(e.target.value); setCity(""); setQ(""); }}>
          <option value="">— Județ —</option>
          {counties.map((x) => <option key={x.county} value={x.county}>{x.county}</option>)}
        </select>
        <select data-testid="locker-pick-city" className={sel} value={city} disabled={!county} onChange={(e) => { setCity(e.target.value); setQ(""); }}>
          <option value="">— Oraș —</option>
          {cities.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <div className="relative">
          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-zinc-500" />
          <input data-testid="locker-pick-search" className={`${sel} pl-7`} placeholder="ex: OMV, Lidl" value={q} onChange={(e) => setQ(e.target.value)} disabled={!city} />
        </div>
      </div>
      {city && <p className="text-[11px] text-zinc-500" data-testid="locker-pick-count">{items.length >= 100 ? "Primele 100 de lockere" : `${items.length} lockere`} în {city}{q.trim() ? ` pentru „${q.trim()}”` : ""}</p>}
      <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
        {shown.map((l) => (
          <div key={l.id} className="flex items-center gap-3 rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2" data-testid={`locker-pick-item-${l.id}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-zinc-100">{l.name}</p>
              <p className="truncate text-[11px] text-zinc-500">{l.address}</p>
            </div>
            {l.maps_url && <a href={l.maps_url} target="_blank" rel="noreferrer" className="text-[11px] text-sky-300 hover:underline">Hartă</a>}
            <button data-testid={`locker-pick-select-${l.id}`} disabled={!!busy || String(order.locker_id) === l.id} onClick={() => pick(l)}
              className="inline-flex items-center gap-1 rounded-md bg-sky-500 px-2.5 py-1 text-[11px] font-semibold text-zinc-950 disabled:opacity-50">
              {busy === l.id && <Loader2 className="h-3 w-3 animate-spin" />}{String(order.locker_id) === l.id ? "Selectat" : "Selectează"}
            </button>
          </div>
        ))}
      </div>
      <button data-testid="locker-pick-cancel-btn" onClick={() => setOpen(false)} className="text-[11px] text-zinc-500 hover:text-zinc-300">Renunță</button>
    </div>
  );
};
